import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Skeleton } from './ui/skeleton';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Calendar, Plus, ImageIcon, Pencil, Trash2, AlertTriangle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import BannerCreateModal from './BannerCreateModal';
import BannerEditModal from './BannerEditModal';

const STATUS_BADGE = {
  active: { labelKey: 'banner.statusActive', className: 'bg-green-50 text-green-700 border-green-200' },
  expired: { labelKey: 'banner.statusExpired', className: 'bg-gray-100 text-gray-600 border-gray-200' },
  taken_down: { labelKey: 'banner.statusTakenDown', className: 'bg-red-50 text-red-700 border-red-200' },
};

const daysLeft = (expiresAt) => {
  const ms = new Date(expiresAt).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
};

/**
 * Employer "My Banners" ROI view (spec §10 / plan Task 7). Lists the caller's own
 * banners from GET /api/banners/mine (image, linked job title, status, days left,
 * click count). Dormant like the rest of the feature — renders nothing until the
 * employer has bought at least one banner (mirrors PastHiresSection's pattern).
 *
 * The API response is the raw `employer_banners` row (no job title), so this
 * component enriches with a direct, RLS-scoped read of the employer's own `jobs`
 * rows for the referenced job_ids (same pattern BannerCreateModal already uses).
 */
const MyBannersSection = ({ profile }) => {
  const { t } = useTranslation();
  const [banners, setBanners] = useState([]);
  const [jobTitles, setJobTitles] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchMine = async () => {
    if (!profile?.id) { setLoading(false); return; }
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) { setLoading(false); return; }

      const res = await api.get('/api/banners/mine', {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });
      const list = res.data?.banners || [];
      setBanners(list);

      const jobIds = Array.from(new Set(list.map((b) => b.job_id).filter(Boolean)));
      if (jobIds.length > 0) {
        const { data: jobs, error } = await supabase
          .from('jobs')
          .select('id, title, slug, location, locations, salary_min, salary_max, salary_period')
          .eq('employer_id', profile.id)
          .in('id', jobIds);
        if (!error && jobs) {
          const map = {};
          jobs.forEach((j) => { map[j.id] = j; });
          setJobTitles(map);
        }
      }
    } catch (error) {
      console.error('[MyBannersSection] Failed to fetch banners:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMine();
  }, [profile?.id]); // fetchMine intentionally omitted — it's redefined each render but stable in behavior

  const handleDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      await api.delete(`/api/banners/${deleteTarget.id}`, {
        headers: { Authorization: `Bearer ${session?.session?.access_token}` },
      });
      toast.success(t('banner.deleteSuccess', 'Banner deleted.'));
      setDeleteTarget(null);
      fetchMine();
    } catch (error) {
      console.error('[MyBannersSection] Delete failed:', error);
      toast.error(t('banner.deleteError', 'Failed to delete the banner. Please try again.'));
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div>
        <h2 className="font-heading text-2xl font-semibold mb-4">{t('banner.myBannersTitle')}</h2>
        <Card><Skeleton className="h-32" /></Card>
      </div>
    );
  }

  return (
    <>
      <div data-testid="my-banners-section">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-heading text-2xl font-semibold">
            {t('banner.myBannersTitle')}{banners.length > 0 ? ` (${banners.length})` : ''}
          </h2>
          <Button size="sm" variant="outline" onClick={() => setShowCreateModal(true)}>
            <Plus size={16} className="mr-1" />
            {t('banner.createBanner')}
          </Button>
        </div>
        {banners.length === 0 ? (
          <Card className="border-dashed">
            <div className="p-8 flex flex-col items-center text-center gap-3">
              <div className="rounded-full bg-muted p-3">
                <ImageIcon className="text-muted-foreground" size={24} />
              </div>
              <p className="text-sm font-medium">{t('banner.myBannersEmpty')}</p>
              <p className="text-sm text-muted-foreground max-w-md">{t('banner.introBody')}</p>
              <Button size="sm" onClick={() => setShowCreateModal(true)}>
                <Plus size={16} className="mr-1" />
                {t('banner.createBanner')}
              </Button>
            </div>
          </Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {banners.map((b) => {
            const job = jobTitles[b.job_id];
            const badge = STATUS_BADGE[b.status] || STATUS_BADGE.expired;
            return (
              <Card key={b.id} className="overflow-hidden">
                <div className="aspect-[3/1] bg-muted flex items-center justify-center">
                  {b.image_url ? (
                    <img src={b.image_url} alt={job?.title || ''} className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="text-muted-foreground" size={24} />
                  )}
                </div>
                <div className="p-3 space-y-2">
                  <p className="text-sm font-medium truncate" title={job?.title}>
                    {job?.title || t('banner.myBannersTitle')}
                  </p>
                  <div className="flex items-center justify-between flex-wrap gap-1">
                    <Badge variant="outline" className={badge.className}>{t(badge.labelKey)}</Badge>
                    {b.status === 'active' && (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Calendar size={12} />
                        {t('banner.daysLeft', { count: daysLeft(b.expires_at) })}
                      </span>
                    )}
                  </div>
                  {/* Performance metrics (clicks/applications) are intentionally NOT
                      shown to employers yet — admin-proprietary until proven (spec §12). */}
                  {b.status === 'active' && (
                    <div className="flex gap-2 pt-1 border-t border-dashed">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 text-xs"
                        onClick={() => setEditTarget(b)}
                        data-testid="banner-edit-btn"
                      >
                        <Pencil size={13} className="mr-1" />
                        {t('banner.editBanner', 'Edit')}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 text-xs text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                        onClick={() => setDeleteTarget(b)}
                        data-testid="banner-delete-btn"
                      >
                        <Trash2 size={13} className="mr-1" />
                        {t('banner.deleteBanner', 'Delete')}
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
          </div>
        )}
      </div>

      {showCreateModal && (
        <BannerCreateModal onClose={() => { setShowCreateModal(false); fetchMine(); }} />
      )}

      {editTarget && (
        <BannerEditModal
          banner={editTarget}
          job={jobTitles[editTarget.job_id]}
          onClose={(didSave) => { setEditTarget(null); if (didSave) fetchMine(); }}
        />
      )}

      {/* Delete confirm — explicit NO-REFUND warning (spec §5). To feature a different
          job, the employer creates a new banner instead. */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open && !deleting) setDeleteTarget(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 size={20} />
              {t('banner.deleteConfirmTitle', 'Delete this banner?')}
            </DialogTitle>
            <DialogDescription>
              {t('banner.deleteConfirmBody', 'To feature a different job, create a new banner instead.')}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            <AlertTriangle size={16} className="mt-0.5 shrink-0 text-red-600" />
            <span>{t('banner.deleteConfirmWarning', 'Your credits will not be refunded. This removes it from the homepage right away.')}</span>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" disabled={deleting} onClick={() => setDeleteTarget(null)}>
              {t('banner.keepBanner', 'Keep it')}
            </Button>
            <Button
              variant="destructive"
              disabled={deleting}
              onClick={handleDelete}
              data-testid="confirm-banner-delete-btn"
            >
              {deleting ? '...' : t('banner.deleteConfirmAction', 'Delete, no refund')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MyBannersSection;
